import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { CompraSituacaoRequest } from '../../requests/orquestrador-compras/compra-situacao.request';
import { CompraIntegracaoExcluirRequest } from '../../requests/orquestrador-compras/compra-integracao-excluir.request';
import { CompraEstoqueSituacaoResponse } from '../../responses/orquestrador-compras/compra-estoque-situacao.response';
import { CompraFinanceiroSituacaoResponse } from '../../responses/orquestrador-compras/compra-financeiro-situacao.response';
import { CompraIntegracaoResponse } from '../../responses/orquestrador-compras/compra-integracao.response';
import { GetItensFornecedorResponse } from '@shared/services/responses/orquestrador-compras/get-itens-fornecedor.response';

/**
 * Controller `Compra` do OrquestradorCompras: integração da compra com o estoque e o financeiro (origem Pedido)
 * e os itens comprados do fornecedor.
 */
@Injectable({ providedIn: 'root' })
export class OrquestradorCompraService extends CommonService {
  private readonly apiUrl: string = environment.urlApiOrquestradorCompras + 'Compra';

  constructor(private httpClient: HttpClient) {
    super();
  }

  /** Movimentações de estoque lançadas por cada compra (consulta ao Produto pelo orquestrador). */
  public consultarSituacaoEstoque(idsCompra: number[]): Observable<CompraEstoqueSituacaoResponse> {
    const request = new CompraSituacaoRequest();
    request.idsCompra = idsCompra;
    return this.httpClient
      .post<CompraEstoqueSituacaoResponse>(`${this.apiUrl}/estoque/situacao`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Contas a pagar lançadas por cada compra (consulta ao Finanças pelo orquestrador). */
  public consultarSituacaoFinanceira(idsCompra: number[]): Observable<CompraFinanceiroSituacaoResponse> {
    const request = new CompraSituacaoRequest();
    request.idsCompra = idsCompra;
    return this.httpClient
      .post<CompraFinanceiroSituacaoResponse>(`${this.apiUrl}/financeiro/situacao`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Últimos itens comprados do fornecedor (ordens de compra e notas de entrada), com pesquisa opcional pela
   * descrição: aba Produtos das últimas compras do cadastro de Pessoa.
   */
  public getItensDoFornecedor(idPessoaFornecedor: number, pesquisa: string): Observable<GetItensFornecedorResponse> {
    return this.httpClient
      .get<GetItensFornecedorResponse>(
        `${this.apiUrl}/fornecedor/idPessoa/${idPessoaFornecedor}/itens?pesquisa=${encodeURIComponent(pesquisa)}`,
        this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public lancarEstoque(idCompra: number): Observable<CompraIntegracaoResponse> {
    return this.httpClient
      .post<CompraIntegracaoResponse>(`${this.apiUrl}/idCompra/${idCompra}/estoque/lancar`, null, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public excluirEstoque(idCompra: number, request: CompraIntegracaoExcluirRequest): Observable<CompraIntegracaoResponse> {
    return this.httpClient
      .post<CompraIntegracaoResponse>(`${this.apiUrl}/idCompra/${idCompra}/estoque/excluir`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public lancarFinanceiro(idCompra: number): Observable<CompraIntegracaoResponse> {
    return this.httpClient
      .post<CompraIntegracaoResponse>(`${this.apiUrl}/idCompra/${idCompra}/financeiro/lancar`, null, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public excluirFinanceiro(idCompra: number, request: CompraIntegracaoExcluirRequest): Observable<CompraIntegracaoResponse> {
    return this.httpClient
      .post<CompraIntegracaoResponse>(`${this.apiUrl}/idCompra/${idCompra}/financeiro/excluir`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}
